<template>
	<EventLayout :event="event" :subtitle="stage.title" :return-url="route('events.stages.registration.index', event.id)" :return-label="trans('events.stages.registration.title')">
		<template #actions>
			<PrimaryButton :color="Color.Green" @click.stop="update">
				<i class="fa-solid fa-save mr-2"/>
				{{ trans('general.save') }}
			</PrimaryButton>
		</template>
		<EditStage :stage="stage">
			<template #switches>
				<Switch v-model.number="stage.refundable" label="events.prices.refundable"/>
				<Switch v-model.number="stage.show_availability" label="events.prices.show_availability"/>
				<Switch v-model.number="stage.early_enabled" :label="trans('events.stages.registration.early_bird')"/>
			</template>
			<Card :title="trans('events.stages.registration.selection_type')">
				<div class="flex flex-col lg:flex-row items-stretch lg:items-end justify-start lg:place-self-start gap-2">
					<Switch v-model.number="stage.selection_mandatory" label="events.selections.mandatory"/>
					<LabeledObject>
						<template #label>{{ trans('events.stages.registration.maximum') }}</template>
						<IconNumberInput v-model.number="stage.maximum" icon-class="fa-solid fa-ticket" full/>
					</LabeledObject>
					<LabeledObject>
						<template #label>{{ trans('events.selections.types.title') }}</template>
						<select v-model="stage.selection_type">
							<option value="multiple">{{ trans('events.selections.types.multiple') }}</option>
							<option value="single">{{ trans('events.selections.types.single') }}</option>
						</select>
					</LabeledObject>
					<LabeledObject v-if="stage.selection_type === 'multiple'">
						<template #label>{{ trans('events.selections.limit') }}</template>
						<NumberInput v-model.number="stage.selection_limit"/>
					</LabeledObject>
				</div>
			</Card>
			<div class="grid grid-cols-1 lg:grid-cols-3 gap-3">
				<Card :title="trans('events.stages.registration.restrictions.title')" :subtitle="trans('events.stages.registration.restrictions.description')">
					<div class="grid grid-cols-1 lg:grid-cols-2 gap-3">
						<Switch v-model.number="stage.general_enabled" :label="trans('events.stages.registration.prices.general')"/>
						<Switch v-model.number="stage.local_enabled" :label="trans('events.stages.registration.prices.local')"/>
						<Switch v-model.number="stage.international_enabled" :label="trans('events.stages.registration.prices.international')"/>
						<Switch v-model.number="stage.representative_enabled" :label="trans('events.stages.registration.prices.representative')"/>
						<Switch v-model.number="stage.organizer_enabled" :label="trans('events.stages.registration.prices.organizer')"/>
						<Switch v-model.number="stage.workgroup_enabled" :label="trans('events.stages.registration.prices.workgroup')"/>
						<Switch v-model.number="stage.board_enabled" :label="trans('events.stages.registration.prices.board')"/>
						<Switch v-model.number="stage.dinosaur_enabled" :label="trans('events.stages.registration.prices.dinosaur')"/>
					</div>
					<LabeledObject :description="trans('events.prices.payment_timeout.description')">
						<template #label>{{ trans('events.prices.payment_timeout.title') }}</template>
						<div class="grow flex gap-2">
							<Switch v-model="showPaymentTimeout" :label="trans(showPaymentTimeout ? 'general.enabled' : 'general.disabled')"/>
							<IconNumberInput v-if="showPaymentTimeout" v-model="stage.payment_timeout" icon-class="fa-solid fa-clock" :min="0" :max="4294967295" :step="1" full/>
						</div>
					</LabeledObject>
				</Card>
				<Card :title="trans('events.prices.early_birds')">
					<div class="grid grid-cols-1 lg:grid-cols-2 gap-3">
						<LabeledObject>
							<template #label>{{ trans('events.stages.registration.prices.general') }}</template>
							<IconNumberInput v-if="stage.general_enabled" v-model="stage.early_general_price" icon-class="fa-solid fa-dollar-sign" full/>
							<div v-else class="input-like text-gray-600 dark:text-gray-400">{{ trans('general.disabled') }}</div>
						</LabeledObject>
						<LabeledObject>
							<template #label>{{ trans('events.stages.registration.prices.local') }}</template>
							<IconNumberInput v-if="stage.local_enabled" v-model="stage.early_local_price" icon-class="fa-solid fa-dollar-sign" full/>
							<div v-else class="input-like text-gray-600 dark:text-gray-400">{{ trans('general.disabled') }}</div>
						</LabeledObject>
						<LabeledObject>
							<template #label>{{ trans('events.stages.registration.prices.international') }}</template>
							<IconNumberInput v-if="stage.international_enabled" v-model="stage.early_international_price" icon-class="fa-solid fa-dollar-sign" full/>
							<div v-else class="input-like text-gray-600 dark:text-gray-400">{{ trans('general.disabled') }}</div>
						</LabeledObject>
						<LabeledObject>
							<template #label>{{ trans('events.stages.registration.prices.representative') }}</template>
							<IconNumberInput v-if="stage.representative_enabled" v-model="stage.early_representative_price" icon-class="fa-solid fa-dollar-sign" full/>
							<div v-else class="input-like text-gray-600 dark:text-gray-400">{{ trans('general.disabled') }}</div>
						</LabeledObject>
						<LabeledObject>
							<template #label>{{ trans('events.stages.registration.prices.organizer') }}</template>
							<IconNumberInput v-if="stage.organizer_enabled" v-model="stage.early_organizer_price" icon-class="fa-solid fa-dollar-sign" full/>
							<div v-else class="input-like text-gray-600 dark:text-gray-400">{{ trans('general.disabled') }}</div>
						</LabeledObject>
						<LabeledObject>
							<template #label>{{ trans('events.stages.registration.prices.workgroup') }}</template>
							<IconNumberInput v-if="stage.workgroup_enabled" v-model="stage.early_workgroup_price" icon-class="fa-solid fa-dollar-sign" full/>
							<div v-else class="input-like text-gray-600 dark:text-gray-400">{{ trans('general.disabled') }}</div>
						</LabeledObject>
						<LabeledObject>
							<template #label>{{ trans('events.stages.registration.prices.board') }}</template>
							<IconNumberInput v-if="stage.board_enabled" v-model="stage.early_board_price" icon-class="fa-solid fa-dollar-sign" full/>
							<div v-else class="input-like text-gray-600 dark:text-gray-400">{{ trans('general.disabled') }}</div>
						</LabeledObject>
						<LabeledObject>
							<template #label>{{ trans('events.stages.registration.prices.dinosaur') }}</template>
							<IconNumberInput v-if="stage.dinosaur_enabled" v-model="stage.early_dinosaur_price" icon-class="fa-solid fa-dollar-sign" full/>
							<div v-else class="input-like text-gray-600 dark:text-gray-400">{{ trans('general.disabled') }}</div>
						</LabeledObject>
					</div>
				</Card>
				<Card :title="trans('events.prices.latecomers')">
					<div class="grid grid-cols-1 lg:grid-cols-2 gap-3">
						<LabeledObject>
							<template #label>{{ trans('events.stages.registration.prices.general') }}</template>
							<IconNumberInput v-if="stage.general_enabled" v-model="stage.general_price" icon-class="fa-solid fa-dollar-sign" full/>
							<div v-else class="input-like text-gray-600 dark:text-gray-400">{{ trans('general.disabled') }}</div>
						</LabeledObject>
						<LabeledObject>
							<template #label>{{ trans('events.stages.registration.prices.local') }}</template>
							<IconNumberInput v-if="stage.local_enabled" v-model="stage.local_price" icon-class="fa-solid fa-dollar-sign" full/>
							<div v-else class="input-like text-gray-600 dark:text-gray-400">{{ trans('general.disabled') }}</div>
						</LabeledObject>
						<LabeledObject>
							<template #label>{{ trans('events.stages.registration.prices.international') }}</template>
							<IconNumberInput v-if="stage.international_enabled" v-model="stage.international_price" icon-class="fa-solid fa-dollar-sign" full/>
							<div v-else class="input-like text-gray-600 dark:text-gray-400">{{ trans('general.disabled') }}</div>
						</LabeledObject>
						<LabeledObject>
							<template #label>{{ trans('events.stages.registration.prices.representative') }}</template>
							<IconNumberInput v-if="stage.representative_enabled" v-model="stage.representative_price" icon-class="fa-solid fa-dollar-sign" full/>
							<div v-else class="input-like text-gray-600 dark:text-gray-400">{{ trans('general.disabled') }}</div>
						</LabeledObject>
						<LabeledObject>
							<template #label>{{ trans('events.stages.registration.prices.organizer') }}</template>
							<IconNumberInput v-if="stage.organizer_enabled" v-model="stage.organizer_price" icon-class="fa-solid fa-dollar-sign" full/>
							<div v-else class="input-like text-gray-600 dark:text-gray-400">{{ trans('general.disabled') }}</div>
						</LabeledObject>
						<LabeledObject>
							<template #label>{{ trans('events.stages.registration.prices.workgroup') }}</template>
							<IconNumberInput v-if="stage.workgroup_enabled" v-model="stage.workgroup_price" icon-class="fa-solid fa-dollar-sign" full/>
							<div v-else class="input-like text-gray-600 dark:text-gray-400">{{ trans('general.disabled') }}</div>
						</LabeledObject>
						<LabeledObject>
							<template #label>{{ trans('events.stages.registration.prices.board') }}</template>
							<IconNumberInput v-if="stage.board_enabled" v-model="stage.board_price" icon-class="fa-solid fa-dollar-sign" full/>
							<div v-else class="input-like text-gray-600 dark:text-gray-400">{{ trans('general.disabled') }}</div>
						</LabeledObject>
						<LabeledObject>
							<template #label>{{ trans('events.stages.registration.prices.dinosaur') }}</template>
							<IconNumberInput v-if="stage.dinosaur_enabled" v-model="stage.dinosaur_price" icon-class="fa-solid fa-dollar-sign" full/>
							<div v-else class="input-like text-gray-600 dark:text-gray-400">{{ trans('general.disabled') }}</div>
						</LabeledObject>
					</div>
				</Card>
			</div>
		</EditStage>
	</EventLayout>
</template>
<script setup lang="ts">
import axios from 'axios'
import {route} from 'ziggy-js'
import {trans} from 'laravel-vue-i18n'
import {Color} from '@/Types/Enums/Color'
import {type Event} from '@/Types/Events/Event'
import Card from '@/Components/UI/Cards/Card.vue'
import EventLayout from '@/Layouts/EventLayout.vue'
import {defineProps, type Ref, ref, watch} from 'vue'
import Switch from '@/Components/UI/Switches/Switch.vue'
import LabeledObject from '@/Components/UI/LabeledObject.vue'
import NumberInput from '@/Components/UI/Inputs/NumberInput.vue'
import EditStage from '@/Components/Events/Stages/EditStage.vue'
import PrimaryButton from '@/Components/UI/Buttons/PrimaryButton.vue'
import IconNumberInput from '@/Components/UI/Inputs/IconNumberInput.vue'
import {showInfo, showSuccess, showValidationErrors} from '@/Helpers/general'
import {type RegistrationStage} from '@/Types/Events/Stages/RegistrationStage'

const props = defineProps<{
	event:Event,
	stage:RegistrationStage,
}>()

const event:Ref<Event> = ref(props.event)
const working:Ref<boolean> = ref(false)
const stage:Ref<RegistrationStage> = ref(props.stage)
const showPaymentTimeout:Ref<number|null> = ref(stage.value.payment_timeout !== null)

watch(showPaymentTimeout, (value) => {
	stage.value.payment_timeout = value ? 0 : null
})

function update():void{
	if(!working.value){
		working.value = true
		showInfo(trans('general.updating'))
		axios.patch(route('events.stages.registration.update', {
			event: event.value.id,
			stage: stage.value.id
		}), stage.value).then(() => {
			showSuccess(trans('general.updated'))
		}).catch(showValidationErrors).finally(() => {
			working.value = false
		})
	}
}
</script>